import { Link } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, ChevronRight, Check, ShieldPlus, Stethoscope, Sparkles } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BOOKING_URL } from '@/config/site';

const specialistServices = [
  {
    id: 'implants',
    title: 'Dental Implants',
    subtitle: 'Permanent tooth replacement',
    price: 'from £2,700',
    description: 'Dental implants are the gold standard for replacing missing teeth. A titanium post is placed into the jawbone, providing a stable foundation for a natural-looking crown. The result: teeth that look, feel, and function like your own.',
    benefits: ['Natural appearance', 'Preserves jawbone', 'Long-lasting solution', 'No impact on adjacent teeth'],
    pricing: [
      { item: 'Single implant crown (implant, abutment & crown)', price: 'from £2,700' },
      { item: 'Implant consultation', price: '£160' },
      { item: 'Crown on existing implant', price: '£1,500' },
    ],
    image: '/images/services/dentalimplants.png',
  },
  {
    id: 'full-arch',
    title: 'Full Arch Reconstruction',
    subtitle: 'Complete smile restoration',
    price: 'from £5,000',
    description: 'For patients missing multiple teeth or facing full tooth loss, full arch reconstruction offers a life-changing solution. Using strategically placed implants, we can restore an entire arch of teeth, often in a single day.',
    benefits: ['Complete smile restoration', 'Improved chewing function', 'Enhanced confidence', 'Stable, secure fit'],
    pricing: [
      { item: 'Lower implant overdenture / full arch implant bridge', price: 'from £5,000' },
      { item: 'Comprehensive restorative consultation', price: '£180' },
    ],
    image: '/images/services/fullarchreconstruction.png',
  },
  {
    id: 'bone-grafting',
    title: 'Bone Grafting',
    subtitle: 'Building a strong foundation',
    price: '£1,500',
    description: 'Adequate bone is essential for successful implants. If you\'ve experienced bone loss, grafting procedures can rebuild the foundation needed. Our specialist team uses precise, evidence-based techniques for predictable outcomes.',
    benefits: ['Enables implant placement', 'Prevents further bone loss', 'Restores facial structure', 'Proven techniques'],
    pricing: [
      { item: 'Natural Bone Grafting', price: '£1,500' },
    ],
    image: '/images/services/bonegrafting.png',
  },
  {
    id: 'gum-treatment',
    title: 'Periodontal Treatment',
    subtitle: 'Gum disease & recession treatment',
    price: '£400',
    description: 'From gum disease management to recession treatment, our periodontal services restore gum health and protect your teeth. We offer both non-surgical and surgical options tailored to your needs.',
    benefits: ['Treats gum disease', 'Reduces sensitivity', 'Improves aesthetics', 'Prevents progression'],
    pricing: [
      { item: 'Non-surgical treatment (per quadrant)', price: '£400' },
      { item: 'Surgical treatment (per quadrant)', price: '£750' },
      { item: 'Root coverage surgery (single tooth)', price: '£850' },
      { item: 'Crown lengthening (single tooth)', price: '£550' },
    ],
    image: '/images/services/periodontaltreatment.png',
  },
  {
    id: 'sinus-lifting',
    title: 'Sinus Lifting',
    subtitle: 'Preparing for upper jaw implants',
    price: '£1,750',
    description: 'When placing implants in the upper jaw, the sinus cavity can limit available bone. Sinus lift surgery gently raises the sinus floor and adds bone, creating the space needed for successful implant placement.',
    benefits: ['Enables upper jaw implants', 'Minimally invasive approach', 'Predictable healing', 'Surgical precision'],
    pricing: [
      { item: 'Sinus lift surgery (single sinus)', price: '£1,750' },
    ],
    image: '/images/services/sinuslifting.png',
  },
  {
    id: 'surgical-extractions',
    title: 'Surgical Extractions',
    subtitle: 'Complex tooth removal',
    price: '£250',
    description: 'Some teeth require surgical expertise for safe removal, including impacted wisdom teeth, broken roots, or teeth with complex anatomy. Our specialist team handles difficult cases with skill and care.',
    benefits: ['Expert technique', 'Minimised discomfort', 'Faster healing', 'Preservation of surrounding tissue'],
    pricing: [
      { item: 'Surgical extraction', price: '£250 - £396' },
      { item: 'Impacted wisdom tooth extraction', price: '£496' },
      { item: 'IV sedation (per hour)', price: '£350' },
    ],
    image: '/images/services/surgicalextractions.png',
  },
  {
    id: 'apicectomy',
    title: 'Apicectomy',
    subtitle: 'Root end surgery',
    price: '£1,200',
    description: 'When root canal treatment isn\'t sufficient, an apicectomy can save the tooth. This microsurgical procedure removes the infected root tip and seals the canal, often preserving teeth that would otherwise need extraction.',
    benefits: ['Saves natural teeth', 'Precise microsurgery', 'High success rate', 'Minimal recovery'],
    pricing: [
      { item: 'Apicectomy procedure', price: '£1,200' },
    ],
    image: '/images/services/apicectomy.png',
  },
  {
    id: 'root-canal',
    title: 'Endodontics (Root Canal Treatment)',
    subtitle: 'Specialist endodontic care',
    price: 'from £950',
    description: 'Our specialist root canal treatments save teeth that might otherwise need extraction. Using advanced techniques and magnification, we achieve high success rates even in complex cases.',
    benefits: ['Saves natural teeth', 'Relieves pain', 'High success rate', 'Advanced techniques'],
    pricing: [
      { item: 'Root canal treatment (RCT)', price: '£950 - £1,100' },
      { item: 'Retreatment (Re-RCT)', price: '£1,200' },
      { item: 'Endodontic consultation', price: '£160' },
    ],
    image: '/images/services/endodontics.png',
  },
];

const generalServices = [
  {
    id: 'examinations',
    title: 'Examinations & Consultations',
    subtitle: 'Your dental health assessment',
    price: '£49',
    description: 'Comprehensive examinations to assess your oral health, identify any concerns early, and create a personalised treatment plan.',
    image: 'https://images.unsplash.com/photo-1629909615184-74f495363b67?w=600&q=80',
    pricing: [
      { item: 'New patient examination (18+)', price: '£85' },
      { item: 'New patient examination (under 18)', price: '£55' },
      { item: 'Existing patient examination', price: '£49' },
      { item: 'Emergency appointment (incl. X-rays)', price: '£90' },
    ],
  },
  {
    id: 'hygiene',
    title: 'Hygiene & Prevention',
    subtitle: 'Professional cleaning & care',
    price: '£87',
    description: 'Regular hygiene appointments are essential for maintaining healthy teeth and gums. Our hygienists provide thorough cleaning and personalised advice.',
    image: '/images/services/hygiene.jpeg',
    pricing: [
      { item: 'Hygienist session', price: '£87' },
      { item: 'Airflow & hygienist session', price: '£150' },
      { item: 'Child hygienist session', price: '£50' },
      { item: 'Mouth guard', price: '£200' },
      { item: 'Bite guard (Michigan/Tanner)', price: '£350' },
    ],
  },
  {
    id: 'fillings',
    title: 'Fillings & Restorations',
    subtitle: 'Repair and restore',
    price: '£95',
    description: 'From simple fillings to complex restorations, we use high-quality materials to repair damage and restore function to your teeth.',
    image: 'https://images.unsplash.com/photo-1598256989800-fe5f95da9787?w=600&q=80',
    pricing: [
      { item: 'Composite filling (small)', price: '£120' },
      { item: 'Composite filling (medium)', price: '£180' },
      { item: 'Composite filling (large)', price: '£220' },
      { item: 'Amalgam filling (small)', price: '£95' },
    ],
  },
  {
    id: 'general-endodontics',
    title: 'Endodontics',
    subtitle: 'Root canal treatment & retreatment',
    price: 'from £950',
    description: 'Specialist-led root canal care to preserve natural teeth, manage pain, and treat infection with predictable outcomes.',
    image: '/images/services/endodontics.png',
    pricing: [
      { item: 'Root canal treatment (RCT)', price: '£950 - £1,100' },
      { item: 'Retreatment (Re-RCT)', price: '£1,200' },
      { item: 'Endodontic consultation', price: '£160' },
    ],
  },
  {
    id: 'crowns',
    title: 'Crowns, Bridges & Veneers',
    subtitle: 'Restore and enhance',
    price: '£650',
    description: 'Custom-made restorations to repair damaged teeth or enhance your smile. We offer a range of materials to suit your needs and budget.',
    image: '/images/services/crownsbridgesveneers.png',
    pricing: [
      { item: 'Porcelain veneer (per tooth)', price: 'from £690' },
      { item: 'Full arch veneers (per tooth)', price: 'from £690' },
      { item: 'Full porcelain crown', price: '£750' },
      { item: 'E-Max crown', price: '£800' },
      { item: 'All ceramic crown', price: '£850' },
      { item: 'Bridgework (per unit)', price: '£650' },
    ],
  },
  {
    id: 'whitening',
    title: 'Teeth Whitening',
    subtitle: 'Brighten your smile',
    price: '£250',
    description: 'Professional whitening treatments for a noticeably brighter smile. Safe, effective, and supervised by our dental team.',
    image: '/images/services/professionalwhitening.png',
    pricing: [
      { item: 'Bleaching (single arch)', price: '£250' },
      { item: 'Bleaching (upper & lower)', price: '£450' },
      { item: 'Home whitening kit', price: '£355' },
      { item: 'Internal bleaching (per tooth)', price: '£250' },
    ],
  },
  {
    id: 'dentures',
    title: 'Dentures',
    subtitle: 'Full and partial options',
    price: '£1,200',
    description: 'Custom-fitted dentures to replace missing teeth. We offer a range of options from acrylic to chrome and flexible Valplast dentures.',
    image: '/images/services/dentures.png',
    pricing: [
      { item: 'Full denture (acrylic)', price: '£1,200 - £2,200' },
      { item: 'Partial denture (acrylic)', price: '£1,200' },
      { item: 'Chrome denture (full/partial)', price: '£1,800' },
      { item: 'Valplast dentures', price: '£1,850' },
    ],
  },
  {
    id: 'extractions',
    title: 'Extractions',
    subtitle: 'Simple tooth removal',
    price: '£150',
    description: 'When a tooth cannot be saved, we ensure extractions are performed gently and efficiently with your comfort as priority.',
    image: '/images/services/surgicalextractions.png',
    pricing: [
      { item: 'Simple extraction', price: '£150' },
      { item: 'Complex extraction', price: '£200' },
      { item: 'Surgical extraction', price: '£250' },
    ],
  },
  {
    id: 'invisalign',
    title: 'Invisalign',
    subtitle: 'Clear aligner orthodontics',
    price: 'from £2,850',
    description: 'Straighten your teeth discreetly with Invisalign clear aligners. Custom-made for your smile with predictable results. Also available as part of our Smile Design pathway.',
    image: '/images/services/invisalign.png',
    pricing: [
      { item: 'Invisalign consultation', price: '£50' },
      { item: 'Single arch treatment', price: 'from £2,850' },
      { item: 'Double arch treatment', price: 'from £3,850' },
    ],
  },
  {
    id: 'nervous-patients-sedation',
    title: 'Sedation for Nervous Patients',
    subtitle: 'Calm, supported treatment appointments',
    price: '£350',
    description: 'For anxious or nervous patients, we provide carefully planned conscious sedation to make treatment more comfortable and manageable.',
    image: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=600&q=80',
    pricing: [
      { item: 'Sedation planning (nervous patients)', price: 'on request' },
      { item: 'IV sedation (per hour)', price: '£350' },
    ],
  },
  {
    id: 'facial-aesthetics',
    title: 'Facial Aesthetics',
    subtitle: 'Existing patients only',
    price: 'on request',
    description: 'Facial aesthetics are available as part of comprehensive treatment planning for existing dental patients.',
    image: 'https://images.unsplash.com/photo-1629909615184-74f495363b67?w=600&q=80',
    pricing: [
      { item: 'Facial aesthetics (existing patients)', price: 'on request' },
    ],
  },
];

const smileDesignServices = [
  {
    id: 'smile-design-consultation',
    title: 'Smile Design Consultation',
    subtitle: 'Aesthetic planning and smile preview',
    price: '£160',
    description: 'A structured cosmetic consultation to assess your smile goals, facial balance, tooth shape, and bite. We create a phased plan so your outcome looks natural, balanced, and long-lasting.',
    image: 'https://images.unsplash.com/photo-1629909615184-74f495363b67?w=600&q=80',
    pricing: [
      { item: 'Smile design consultation', price: '£160' },
      { item: 'Comprehensive smile plan', price: 'from £350' },
    ],
  },
  {
    id: 'composite-bonding',
    title: 'Composite Bonding',
    subtitle: 'Shape, edge, and symmetry refinement',
    price: 'from £280',
    description: 'Conservative cosmetic refinement for chips, spacing, worn edges, and tooth proportions. Bonding is designed to blend naturally with your smile and can be layered as part of a full smile plan.',
    image: '/images/services/compositebonding.png',
    pricing: [
      { item: 'Composite bonding (per tooth)', price: 'from £280' },
      { item: 'Multi-tooth smile refinement', price: 'from £1,200' },
    ],
  },
  {
    id: 'aesthetic-veneers',
    title: 'Veneers & Aesthetic Restorations',
    subtitle: 'Premium ceramic smile enhancement',
    price: 'from £690',
    description: 'For deeper shape and shade transformation, we offer carefully planned porcelain or ceramic restorations designed around your facial features and desired smile style.',
    image: '/images/services/crownsbridgesveneers.png',
    pricing: [
      { item: 'Porcelain veneer (per tooth)', price: 'from £690' },
      { item: 'Full arch veneers (per tooth)', price: 'from £690' },
      { item: 'E-Max crown', price: 'from £800' },
      { item: 'All ceramic crown', price: 'from £850' },
    ],
  },
  {
    id: 'smile-design-invisalign',
    title: 'Invisalign in Smile Design',
    subtitle: 'Alignment as the foundation of aesthetics',
    price: 'from £2,850',
    description: 'When alignment is part of your smile goals, Invisalign can be built into your smile design pathway before final whitening or bonding. Invisalign also remains available as a standalone general treatment.',
    image: '/images/services/invisalign.png',
    pricing: [
      { item: 'Invisalign consultation', price: '£50' },
      { item: 'Single arch treatment', price: 'from £2,850' },
      { item: 'Double arch treatment', price: 'from £3,850' },
    ],
  },
  {
    id: 'smile-whitening',
    title: 'Professional Whitening',
    subtitle: 'Shade enhancement before final detailing',
    price: '£250',
    description: 'Whitening is often the first step in smile design, helping us set your baseline shade before bonding, veneers, or alignment refinements.',
    image: '/images/services/professionalwhitening.png',
    pricing: [
      { item: 'Bleaching (single arch)', price: '£250' },
      { item: 'Bleaching (upper & lower)', price: '£450' },
      { item: 'Home whitening kit', price: '£355' },
    ],
  },
];

export default function Services() {
  const [selectedCategory, setSelectedCategory] = useState('specialist');
  const [displayedCategory, setDisplayedCategory] = useState('specialist');
  const [isContentVisible, setIsContentVisible] = useState(true);
  const [isTabsDocked, setIsTabsDocked] = useState(false);
  const stickyTabsRef = useRef(null);
  const listTopRef = useRef(null);
  const pendingCategoryRef = useRef(null);

  const getListTopTarget = useCallback(() => {
    if (!listTopRef.current) return;
    const headerHeight = 80;
    const stickyHeight = stickyTabsRef.current?.offsetHeight ?? 0;
    const extraSpacing = 12;
    return Math.max(
      0,
      listTopRef.current.getBoundingClientRect().top + window.scrollY - headerHeight - stickyHeight - extraSpacing,
    );
  }, []);

  const scrollToListTop = useCallback((behavior = 'smooth') => {
    const targetTop = getListTopTarget();
    if (typeof targetTop !== 'number') return;
    window.scrollTo({ top: targetTop, behavior });
  }, [getListTopTarget]);

  const shouldAutoAlignListTop = useCallback(() => {
    const targetTop = getListTopTarget();
    if (typeof targetTop !== 'number') return false;
    const distance = Math.abs(window.scrollY - targetTop);
    return distance > 96;
  }, [getListTopTarget]);

  const handleCategoryChange = useCallback((value) => {
    if (value === selectedCategory) return;
    setSelectedCategory(value);
    pendingCategoryRef.current = value;
    setIsContentVisible(false);
    // Wait for tab content swap, then align the list start under sticky controls.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (shouldAutoAlignListTop()) {
          scrollToListTop('smooth');
        }
      });
    });
  }, [scrollToListTop, selectedCategory, shouldAutoAlignListTop]);

  useEffect(() => {
    const stickyTopOffset = 80;

    const updateDockedState = () => {
      if (!stickyTabsRef.current) return;
      const stickyTop = stickyTabsRef.current.getBoundingClientRect().top;
      setIsTabsDocked((prev) => {
        const dockThreshold = stickyTopOffset + 0.5;
        const undockThreshold = stickyTopOffset + 24;
        const nextDocked = prev ? stickyTop <= undockThreshold : stickyTop <= dockThreshold;
        return prev === nextDocked ? prev : nextDocked;
      });
    };

    updateDockedState();
    window.addEventListener('scroll', updateDockedState, { passive: true });
    window.addEventListener('resize', updateDockedState);
    return () => {
      window.removeEventListener('scroll', updateDockedState);
      window.removeEventListener('resize', updateDockedState);
    };
  }, []);

  return (
    <div className="pb-mobile-bar" data-testid="services-page">
      {/* Hero */}
      <section className="bg-stone-50 pt-10 pb-6 md:pt-14 md:pb-8 lg:pt-16 lg:pb-10" data-testid="services-hero">
        <div className="container-luxury">
          <div className="max-w-3xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-caption mb-3 text-accent-gold"
            >
              Our Services
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-display mb-4"
            >
              Specialist, General & Smile Design Dentistry
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-body-large"
            >
              From complex implant surgery to routine check-ups, we provide 
              comprehensive care under one roof. Every treatment delivered with 
              the same attention to detail and patient comfort.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-6"
            >
              <Link to="/referrals" data-testid="services-referral-link" className="inline-flex items-center gap-2 font-body font-medium text-charcoal link-underline">
                Referring clinician? Start a specialist referral
                <ChevronRight size={16} strokeWidth={1.5} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Tabs */}
      <section className="pt-8 pb-16 md:pt-10 md:pb-20 lg:pt-12 lg:pb-24" data-testid="services-tabs">
        <div className="container-luxury">
          <Tabs value={selectedCategory} onValueChange={handleCategoryChange} className="w-full">
            {/* Sticky Category Switcher */}
            <div
              ref={stickyTabsRef}
              className={`sticky top-20 z-30 mb-8 transition-[margin] duration-700 ease-out md:z-40 md:mb-10 ${
                isTabsDocked ? '-mx-4 mb-6 sm:-mx-6 md:mx-0 md:mb-7' : ''
              }`}
            >
              <div
                className={`mx-auto w-full max-w-5xl rounded-2xl border border-stone-200/80 bg-white/90 p-2 shadow-[0_16px_36px_rgba(41,37,36,0.1)] backdrop-blur-md transition-[max-width,border-radius,padding,box-shadow] duration-700 ease-out ${
                  isTabsDocked
                    ? 'max-w-none rounded-none border-x-0 border-t-0 p-1.5 shadow-[0_10px_28px_rgba(41,37,36,0.16)] md:max-w-5xl md:rounded-b-2xl md:rounded-t-none md:border-x md:border-t md:p-2.5 md:shadow-[0_12px_30px_rgba(41,37,36,0.14)]'
                    : 'md:rounded-3xl md:p-3 md:shadow-[0_16px_36px_rgba(41,37,36,0.1)]'
                }`}
              >
                <p
                  className={`text-center font-accent uppercase tracking-[0.18em] text-stone-500 transition-all duration-300 md:tracking-[0.2em] ${
                    isTabsDocked ? 'mb-1 text-[11px] opacity-85 md:mb-1 md:text-sm' : 'mb-2 text-[11px] md:text-sm'
                  }`}
                >
                  Choose Your Care Category
                </p>
                <TabsList
                  className={`grid h-auto w-full grid-cols-3 gap-1.5 border border-stone-200 bg-gradient-to-r from-stone-100 via-stone-50 to-stone-100 transition-all duration-500 ease-out md:gap-2 ${
                    isTabsDocked
                      ? 'rounded-lg p-1 md:rounded-b-xl md:rounded-t-none md:p-1.5'
                      : 'rounded-xl p-1.5 md:rounded-2xl md:p-2'
                  }`}
                >
                  <TabsTrigger
                    value="specialist"
                    data-testid="tab-specialist"
                    className={`rounded-lg px-2 text-center font-body font-semibold leading-tight text-stone-600 transition-all duration-300 data-[state=active]:bg-charcoal data-[state=active]:text-warm-white data-[state=active]:shadow-[0_10px_20px_rgba(20,17,16,0.32)] data-[state=active]:ring-1 data-[state=active]:ring-accent-gold/50 md:rounded-xl ${
                      isTabsDocked ? 'h-11 text-xs md:h-12 md:text-base' : 'h-12 text-xs md:h-14 md:text-base'
                    }`}
                  >
                    <span className="inline-flex items-center justify-center gap-1.5 md:gap-2">
                      <ShieldPlus size={15} strokeWidth={1.8} className="shrink-0 md:size-[17px]" />
                      <span className="md:hidden">Specialist</span>
                      <span className="hidden md:inline">Specialist Services</span>
                    </span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="general"
                    data-testid="tab-general"
                    className={`rounded-lg px-2 text-center font-body font-semibold leading-tight text-stone-600 transition-all duration-300 data-[state=active]:bg-charcoal data-[state=active]:text-warm-white data-[state=active]:shadow-[0_10px_20px_rgba(20,17,16,0.32)] data-[state=active]:ring-1 data-[state=active]:ring-accent-gold/50 md:rounded-xl ${
                      isTabsDocked ? 'h-11 text-xs md:h-12 md:text-base' : 'h-12 text-xs md:h-14 md:text-base'
                    }`}
                  >
                    <span className="inline-flex items-center justify-center gap-1.5 md:gap-2">
                      <Stethoscope size={15} strokeWidth={1.8} className="shrink-0 md:size-[17px]" />
                      <span className="md:hidden">General</span>
                      <span className="hidden md:inline">General Dentistry</span>
                    </span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="smile-design"
                    data-testid="tab-smile-design"
                    className={`rounded-lg px-2 text-center font-body font-semibold leading-tight text-stone-600 transition-all duration-300 data-[state=active]:bg-charcoal data-[state=active]:text-warm-white data-[state=active]:shadow-[0_10px_20px_rgba(20,17,16,0.32)] data-[state=active]:ring-1 data-[state=active]:ring-accent-gold/50 md:rounded-xl ${
                      isTabsDocked ? 'h-11 text-xs md:h-12 md:text-base' : 'h-12 text-xs md:h-14 md:text-base'
                    }`}
                  >
                    <span className="inline-flex items-center justify-center gap-1.5 md:gap-2">
                      <Sparkles size={15} strokeWidth={1.8} className="shrink-0 md:size-[17px]" />
                      <span className="md:hidden">Smile</span>
                      <span className="hidden md:inline">Smile Design</span>
                    </span>
                  </TabsTrigger>
                </TabsList>
              </div>
            </div>
            <div ref={listTopRef} className="h-0" aria-hidden="true" />

            <AnimatePresence
              mode="wait"
              initial={false}
              onExitComplete={() => {
                const nextCategory = pendingCategoryRef.current;
                if (nextCategory && nextCategory !== displayedCategory) {
                  setDisplayedCategory(nextCategory);
                }
                pendingCategoryRef.current = null;
                setIsContentVisible(true);
              }}
            >
              {isContentVisible ? (
                <motion.div
                  key={displayedCategory}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.32, ease: "easeOut" }}
                >
                  {/* Specialist Services */}
                  {displayedCategory === 'specialist' ? (
                    <div className="space-y-20">
                  <div className="text-center max-w-2xl mx-auto mb-12">
                    <p className="text-caption mb-4 text-accent-gold">Specialist Team Care</p>
                    <h2 className="text-headline mb-4">Advanced Surgical Procedures</h2>
                    <p className="text-body">
                      Complex procedures requiring surgical expertise, 
                      performed by our specialist-trained team.
                    </p>
                  </div>

                  {specialistServices.map((service, index) => (
                    <motion.div
                      key={service.id}
                      id={service.id}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-center ${
                        index % 2 === 1 ? '' : ''
                      }`}
                    >
                      <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                        <div className="relative aspect-[4/3] rounded-2xl overflow-hidden">
                          <img
                            src={service.image}
                            alt={service.title}
                            className="w-full h-full object-cover"
                          />
                          {/* Price badge */}
                          <div className="absolute top-4 right-4">
                            <span className="inline-flex items-center px-4 py-2 bg-white/95 backdrop-blur-sm rounded-full shadow-lg">
                              <span className="font-body text-sm font-semibold text-charcoal">{service.price}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                        <p className="text-caption mb-2 text-accent-gold">{service.subtitle}</p>
                        <h3 className="text-subheadline mb-4">{service.title}</h3>
                        <p className="text-body mb-6">{service.description}</p>
                        
                        {/* Benefits */}
                        <ul className="space-y-2 mb-6">
                          {service.benefits.map((benefit, i) => (
                            <li key={i} className="flex items-center gap-3 font-body text-sm text-stone-600">
                              <Check size={16} className="text-accent-gold flex-shrink-0" strokeWidth={2} />
                              {benefit}
                            </li>
                          ))}
                        </ul>

                        {/* Pricing table */}
                        <div className="bg-stone-50 rounded-xl p-5 mb-6">
                          <p className="font-body text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Guide Pricing</p>
                          <div className="space-y-2">
                            {service.pricing.map((item, i) => (
                              <div key={i} className="flex justify-between items-center py-1.5 border-b border-stone-200 last:border-0">
                                <span className="font-body text-sm text-charcoal">{item.item}</span>
                                <span className="font-body text-sm font-semibold text-charcoal">{item.price}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                      </div>
                    </motion.div>
                  ))}
                    </div>
                  ) : null}

                  {/* General Services - Redesigned to match Specialist */}
                  {displayedCategory === 'general' ? (
                    <div className="space-y-20">
                  <div className="text-center max-w-2xl mx-auto mb-12">
                    <p className="text-caption mb-4 text-accent-gold">Complete Care</p>
                    <h2 className="text-headline mb-4">General Dentistry</h2>
                    <p className="text-body">
                      Comprehensive general dental services delivered 
                      with the same care and attention as our specialist treatments.
                    </p>
                  </div>

                  {generalServices.map((service, index) => (
                    <motion.div
                      key={service.id}
                      id={service.id}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-center`}
                    >
                      <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                        <div className="relative aspect-[4/3] rounded-2xl overflow-hidden">
                          <img
                            src={service.image}
                            alt={service.title}
                            className="w-full h-full object-cover"
                          />
                          {/* Price badge */}
                          <div className="absolute top-4 right-4">
                            <span className="inline-flex items-center px-4 py-2 bg-white/95 backdrop-blur-sm rounded-full shadow-lg">
                              <span className="font-body text-sm font-semibold text-charcoal">{service.price}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                        <p className="text-caption mb-2 text-accent-gold">{service.subtitle}</p>
                        <h3 className="text-subheadline mb-4">{service.title}</h3>
                        <p className="text-body mb-6">{service.description}</p>

                        {/* Pricing table */}
                        <div className="bg-stone-50 rounded-xl p-5 mb-6">
                          <p className="font-body text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Guide Pricing</p>
                          <div className="space-y-2">
                            {service.pricing.map((item, i) => (
                              <div key={i} className="flex justify-between items-center py-1.5 border-b border-stone-200 last:border-0">
                                <span className="font-body text-sm text-charcoal">{item.item}</span>
                                <span className="font-body text-sm font-semibold text-charcoal">{item.price}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                      </div>
                    </motion.div>
                  ))}
                    </div>
                  ) : null}

                  {/* Smile Design Services */}
                  {displayedCategory === 'smile-design' ? (
                    <div className="space-y-20">
                  <div className="text-center max-w-3xl mx-auto mb-12">
                    <p className="text-caption mb-4 text-accent-gold">Aesthetic Category</p>
                    <h2 className="text-headline mb-4">Smile Design</h2>
                    <p className="text-body">
                      A tailored cosmetic pathway that can include whitening, bonding, veneers, and Invisalign when alignment is required.
                      Every plan is staged for natural-looking results and long-term function.
                    </p>
                    <div className="mt-5 flex flex-wrap justify-center gap-2">
                      {['Digital planning', 'Whitening', 'Composite bonding', 'Veneers', 'Invisalign integration'].map((item) => (
                        <span
                          key={item}
                          className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 font-body text-xs text-stone-600"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>

                  {smileDesignServices.map((service, index) => (
                    <motion.div
                      key={service.id}
                      id={service.id}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      className="grid items-center gap-8 lg:grid-cols-2 lg:gap-16"
                    >
                      <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                        <div className="relative aspect-[4/3] rounded-2xl overflow-hidden">
                          <img
                            src={service.image}
                            alt={service.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute top-4 right-4">
                            <span className="inline-flex items-center px-4 py-2 bg-white/95 backdrop-blur-sm rounded-full shadow-lg">
                              <span className="font-body text-sm font-semibold text-charcoal">{service.price}</span>
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                        <p className="text-caption mb-2 text-accent-gold">{service.subtitle}</p>
                        <h3 className="text-subheadline mb-4">{service.title}</h3>
                        <p className="text-body mb-6">{service.description}</p>

                        <div className="bg-stone-50 rounded-xl p-5 mb-6">
                          <p className="font-body text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Guide Pricing</p>
                          <div className="space-y-2">
                            {service.pricing.map((item, i) => (
                              <div key={i} className="flex justify-between items-center py-1.5 border-b border-stone-200 last:border-0">
                                <span className="font-body text-sm text-charcoal">{item.item}</span>
                                <span className="font-body text-sm font-semibold text-charcoal">{item.price}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                    </div>
                  ) : null}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </Tabs>
        </div>
      </section>

      {/* Pricing Note */}
      <section className="py-12 bg-stone-100">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto text-center">
            <p className="font-body text-sm text-stone-600">
              All prices are guide prices. Complex treatments may be shown as from pricing due to clinical complexity. 
              A full treatment plan with exact costs will be provided following your consultation. 
              We offer 0% finance options on treatments over £500.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing section-hero-echo-dark-alt" data-testid="services-cta">
        <div className="container-luxury text-center">
          <h2 className="mb-6 font-heading text-3xl font-medium tracking-tight text-warm-white md:text-4xl lg:text-5xl">
            Not Sure What You Need?
          </h2>
          <p className="mx-auto mb-8 max-w-2xl font-body text-lg leading-relaxed text-stone-200 md:text-xl">
            Book a consultation and we'll assess your needs together. 
            No pressure, just honest advice.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={BOOKING_URL}
              data-testid="services-final-book"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-warm-white px-8 font-body font-medium text-charcoal transition-all duration-300 hover:bg-accent-gold-light"
            >
              Book Consultation
              <ArrowRight size={18} strokeWidth={1.5} />
            </a>
            <Link
              to="/contact"
              data-testid="services-contact"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-white/70 bg-white/10 px-8 font-body font-medium text-warm-white transition-all duration-300 hover:bg-white hover:text-charcoal"
            >
              Contact Us
              <ChevronRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
